import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { EndpointExpectedResponseVerb } from '@terra/shared-contracts-endpoints';
import { terraProxyApiClient } from '../../clients/testerClient';
import type { AuditLogFlowFilters } from '../../utils/group-expected-response-observations';

type UseExpectedResponseObservationsProps = {
  applicationId: string;
  threadId: string | undefined;
  enabled?: boolean;
  filters?: AuditLogFlowFilters;
  isStateChanging?: boolean;
};

function toObservationQueryFilters({
  filters,
  isStateChanging,
}: {
  filters?: AuditLogFlowFilters;
  isStateChanging?: boolean;
}) {
  return {
    endpointIds:
      filters && filters.endpoints.length > 0 ? filters.endpoints : undefined,
    expectedVerbs:
      filters && filters.verbs.length > 0
        ? (filters.verbs as EndpointExpectedResponseVerb[])
        : undefined,
    expectedEntities:
      filters && filters.entities.length > 0 ? filters.entities : undefined,
    expectedDescriptions:
      filters && filters.descriptions.length > 0
        ? filters.descriptions
        : undefined,
    isStateChanging,
  };
}

export function useExpectedResponseObservations({
  applicationId,
  threadId,
  enabled = true,
  filters,
  isStateChanging,
}: UseExpectedResponseObservationsProps) {
  const queryFilters = toObservationQueryFilters({ filters, isStateChanging });

  return useQuery({
    queryKey: [
      'expected-response-observations',
      applicationId,
      threadId,
      queryFilters,
    ],
    queryFn: async () => {
      if (!applicationId || !threadId) {
        return {
          observations: [],
          totalCount: 0,
          summary: {
            full: 0,
            statusOnly: 0,
            unmatched: 0,
            endpointCount: 0,
            nonStateChangingCount: 0,
          },
        };
      }

      const response =
        await terraProxyApiClient.getExpectedResponseObservationsByNeoRun(
          applicationId,
          threadId,
          queryFilters,
        );

      if (!response) {
        return {
          observations: [],
          totalCount: 0,
          summary: {
            full: 0,
            statusOnly: 0,
            unmatched: 0,
            endpointCount: 0,
            nonStateChangingCount: 0,
          },
        };
      }

      return response;
    },
    enabled: enabled && !!applicationId && !!threadId,
    // Filters are part of the query key, so changing one starts a fresh query.
    // Keep the previous result while it loads so the filter bar and current rows
    // stay mounted instead of being replaced by the loading state.
    placeholderData: keepPreviousData,
    // Observations are written asynchronously on NEO_RUN_FINISHED, so the first
    // fetch after a run can legitimately come back empty. Keep populated results
    // cached, but treat an empty result as stale so focus/remount refetches it.
    staleTime: (query) =>
      (query.state.data?.observations?.length ?? 0) > 0 ? 5 * 60 * 1000 : 0,
    gcTime: 30 * 60 * 1000,
  });
}

export function useExpectedResponseObservationFilters({
  applicationId,
  threadId,
  enabled = true,
}: {
  applicationId: string;
  threadId: string | undefined;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [
      'expected-response-observation-filters',
      applicationId,
      threadId,
    ],
    queryFn: async () => {
      if (!applicationId || !threadId) {
        return {
          endpointIds: [],
          expectedVerbs: [],
          expectedEntities: [],
          expectedDescriptions: [],
        };
      }

      const response =
        await terraProxyApiClient.getExpectedResponseObservationFiltersByNeoRun(
          applicationId,
          threadId,
        );

      if (!response) {
        return {
          endpointIds: [],
          expectedVerbs: [],
          expectedEntities: [],
          expectedDescriptions: [],
        };
      }

      return response;
    },
    enabled: enabled && !!applicationId && !!threadId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}
